import { filterRecordsByActiveCriteriaInContextFilter } from '@/modules/solutions/solutionsFilters';
import { css } from '@emotion/react';
import includes from 'lodash.includes';
import map from 'lodash.map';
import size from 'lodash.size';
import some from 'lodash.some';
import React, { Fragment } from 'react';
import { ContextFiltersSettings } from '@/modules/core/data/types/ContextFiltersSettings';
import { Asset } from '@/modules/core/data/types/Asset';
import { Campus } from '@/modules/core/data/types/Campus';
import { Contact } from '@/modules/core/data/types/Contact';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { LabeledLink } from '@/modules/core/data/types/LabeledLink';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import ContactsSlider from './ContactsSlider';
import SolutionLinks from './SolutionLinks';
import SolutionStamps from './SolutionStamps';
import AirtableAsset from '@/modules/core/airtable/components/AirtableAsset';

type Props = {
  index: number;
  hasSolutionCardDetails: boolean;
  links: LabeledLink[];
  campuses: Campus[];
  contacts: Contact[];
  context: StudentSolutionContext;
  contextFiltersSettings: ContextFiltersSettings;
  educationalProgramSteps: EducationalProgramStep[];
  isCardDetailsOpen: boolean;
  hasAnyContactToDisplay: boolean;
  logo: Asset;
  setIsCardDetailsOpen: (boolean) => void;
  studentSolution: StudentSolution;
}

const SolutionCardTeaser: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    index,
    hasSolutionCardDetails,
    links,
    campuses,
    contacts,
    context,
    contextFiltersSettings,
    isCardDetailsOpen,
    educationalProgramSteps,
    hasAnyContactToDisplay,
    logo,
    setIsCardDetailsOpen,
    studentSolution,
  }: Props = props;

  return (
    <div
      role="button"
      tabIndex={index}
      className={'card-teaser'}
      css={css`
        margin: 0 45px 0 45px;
        position: absolute;
        cursor: pointer;
        display: inline-flex;
        justify-content: space-between;

        &:focus {
          outline: none;
        }

        .label {
          cursor: default;
        }

        @media (max-width: 991.98px) {
          width: 100%;
          justify-content: left;
          margin: 0;

          .label {
            margin-left: 25px;
          }

          .stamps,
          .links,
          .contact,
          .column-divider {
            display: none;
          }
        }
      `}
      onClick={(event): void => {
        // @ts-ignore
        const targetClassNames = event?.target?.className;
        // console.log('targetClassNames', targetClassNames);
        const allowedTargetClassNames = [
          'card-teaser',
          'contacts-slider',
          'contact-connect',
        ];

        // Check if the event target contains at least one allowed class name.
        // If it doesn't, then it's not an allowed DOM element for triggering the cardDetails open/close event.
        const isTargetClassNameAllowed = some(map(allowedTargetClassNames, (allowedTargetClassName: string) => includes(targetClassNames, allowedTargetClassName)));

        if (isTargetClassNameAllowed && hasSolutionCardDetails) {
          setIsCardDetailsOpen(!isCardDetailsOpen);
        }
      }}
      onKeyPress={(): void => {
        if (hasSolutionCardDetails) {
          setIsCardDetailsOpen(!isCardDetailsOpen);
        }
      }}
    >
      <div>
        {
          logo ? (
            <AirtableAsset
              css={css`
                @media (min-width: 992px) {
                  margin-left: 37px !important;
                }

                @media (max-width: 991.98px) {
                  margin-left: 5px !important;
                }
              `}
              id={studentSolution?.id}
              asset={logo}
              transformationsOverride={{
                width: 80,
              }}
            />
          ) : null
        }
      </div>
      <div className={'column-divider'} />
      <div className={'label'}>
        {studentSolution?.label}
      </div>
      {
        hasAnyContactToDisplay && (
          <Fragment>
            <div className={'column-divider'} />
            <div className={'contact'}>
              <ContactsSlider
                contacts={contacts}
                contactsMode={'minimal'}
              />
            </div>
          </Fragment>
        )
      }
      {
        !!size(links) ? (
          <Fragment>
            <div className={'column-divider'} />
            <div className={'links'}>
              <SolutionLinks links={links} />
            </div>
          </Fragment>
        ) : null
      }
      <div className={'column-divider'} />
      <div className={'stamps'}>
        <SolutionStamps
          contextFiltersSettings={contextFiltersSettings}
          educationalProgramSteps={educationalProgramSteps}
          campuses={campuses}
          categories={filterRecordsByActiveCriteriaInContextFilter(context, studentSolution.categories, 'Category')}
        />
      </div>
    </div>
  );
};

export default SolutionCardTeaser;
